import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { isNil } from "ramda";

import Autocomplete from "@mui/material/Autocomplete";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";

import { postJoinMasterNode } from "redux/handlers/clusterHandler";
import { getServerByType } from "redux/handlers/utilityHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { IServerByTypeAPI } from "types/api/serversApiInterface";

const JoinCluster = ({ server_id }: { server_id: number }): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [servers, setServers] = useState<Array<IServerByTypeAPI>>([]);
 const [selectedServer, setSelectedServer] = useState<number>(0);

 const paperStyle = {
  maxHeight: "fit-content",
  px: 5,
  py: 2,
  minWidth: desktopViewPort ? 300 : "auto",
  cursor: "pointer",
  "&:hover": {
   backgroundColor: "#fafafa"
  }
 };

 const handleOpen = async () => {
  setServers(await dispatch(getServerByType("container-app", server_id)));
  setOpen(true);
 };
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(postJoinMasterNode(server_id, selectedServer));
  setLoading(false);
  handleClose();
 };

 return (
  <>
   <Paper sx={paperStyle} onClick={handleOpen}>
    <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
     <Typography
      color="kxOrange"
      variant="unboundedTitle"
      fontSize={16}
      textTransform="uppercase"
      fontWeight="bold">
      <FormattedMessage id="cluster.addExistingNode" />
     </Typography>
     <IconSelector icon="AddIcon" props={{ color: "kxOrange" }} />
    </Stack>
   </Paper>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "cluster.addExistingNode" })}
    handleClose={handleClose}
    handleConfirm={handleConfirm}
    disabled={loading || selectedServer === 0}>
    <Stack spacing={2}>
     <Typography variant="subtitle2">
      <FormattedMessage id="cluster.selectServer" />
     </Typography>
     <Autocomplete
      autoHighlight
      onChange={(e, value) => (isNil(value) ? setSelectedServer(0) : setSelectedServer(value.id))}
      options={servers.map((element) => {
       return {
        label: element.server_name,
        id: element.id
       };
      })}
      renderInput={(params) => (
       <TextField
        {...params}
        label={intl.formatMessage({ id: "app.servers" })}
        InputLabelProps={{ shrink: true }}
       />
      )}
     />
    </Stack>
   </AppModal>
  </>
 );
};

export default JoinCluster;
